import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ContactRelationship {
  id: string;
  from_contact_id: string;
  to_contact_id: string;
  relationship_kind: 'family' | 'friend';
  relationship_type?: string;
  metadata?: Record<string, any>;
  created_at?: string;
  to_contact?: {
    id: string;
    first_name: string;
    last_name?: string;
    primary_email?: string;
    primary_phone?: string;
  };
}

export function useContactRelationships(contactId?: string) {
  const queryClient = useQueryClient();

  const { data: relationships = [], isLoading } = useQuery({
    queryKey: ['contact-relationships', contactId],
    queryFn: async () => {
      if (!contactId) return [];
      
      const { data, error } = await supabase
        .from('contact_relationships')
        .select(`
          *,
          to_contact:contacts!to_contact_id(
            id,
            first_name,
            last_name,
            primary_email,
            primary_phone
          )
        `)
        .eq('from_contact_id', contactId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as ContactRelationship[];
    },
    enabled: !!contactId,
  });

  const createRelationship = useMutation({
    mutationFn: async (relationship: Omit<ContactRelationship, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('contact_relationships')
        .insert(relationship)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-relationships', contactId] });
      toast.success('Relacionamento adicionado');
    },
    onError: (error: any) => {
      toast.error('Erro ao adicionar relacionamento');
      console.error('Error creating relationship:', error);
    },
  });

  const deleteRelationship = useMutation({
    mutationFn: async (relationshipId: string) => {
      const { error } = await supabase
        .from('contact_relationships')
        .delete()
        .eq('id', relationshipId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-relationships', contactId] });
      toast.success('Relacionamento removido');
    },
    onError: (error: any) => {
      toast.error('Erro ao remover relacionamento');
      console.error('Error deleting relationship:', error);
    },
  });

  return {
    relationships,
    isLoading,
    createRelationship: createRelationship.mutateAsync,
    deleteRelationship: deleteRelationship.mutateAsync,
  };
}
